/**
 * What Ask can see about the page you opened it from.
 *
 * Every analytics page already writes its own state into the query string —
 * selected agents, filter clauses, the time range, the open run or span — so
 * that a link reproduces the view. That makes the URL the one honest,
 * always-current description of "what am I looking at", with no new plumbing
 * and no chance of a stale copy.
 *
 * Called once, when a prompt is submitted. Nothing is computed for display and
 * nothing is transmitted while someone is only looking at a page.
 */

/** Page ids Ask understands, keyed off the route. */
const ROUTE_PAGES = [
  [/\/agent-analytics\/runs/, 'runs', 'Runs'],
  [/\/agent-analytics\/traces/, 'traces', 'Traces'],
  [/\/agent-analytics\/conversations/, 'conversations', 'Conversations'],
  [/\/agent-analytics\/overview/, 'overview', 'Overview'],
  [/\/agent-analytics\/issues/, 'issues', 'Issues'],
  [/\/agent-analytics\/evals/, 'evals', 'Evals'],
  [/\/agent-analytics\/capabilities/, 'capabilities', 'Capabilities'],
  [/\/activity\/users/, 'users', 'Users'],
  [/\/activity\/events/, 'events', 'Events'],
  [/\/agents/, 'agents', 'Agents'],
  [/\/prompts/, 'prompts', 'Prompts'],
  [/\/datasets/, 'datasets', 'Datasets'],
  [/\/analytics\/reports/, 'reports', 'Reports'],
  // Reports and the Overview are boards; the server knows them as "dashboards".
  [/\/[^/]+\/reports(\/|$)/, 'dashboards', 'Reports'],
  [/\/[^/]+\/overview(\/|$)/, 'dashboards', 'Overview'],
  [/\/playground/, 'playground', 'Playground'],
];

function decodeJsonParam(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch (_) {
    return null;
  }
}

/**
 * Build the context object for the current location.
 * @param {{pathname: string, search: string}} location
 */
export function readScreenContext(location) {
  const pathname = location?.pathname || '';
  const params = new URLSearchParams(location?.search || '');

  const match = ROUTE_PAGES.find(([re]) => re.test(pathname));
  if (!match) return null;
  const [, page, pageLabel] = match;

  // The focused record, when a run, span or conversation is open.
  let focus = null;
  if (params.get('span_id')) {
    focus = {
      kind: 'span',
      id: params.get('span_id'),
      started_at: params.get('span_started_at') || null,
      run_id: params.get('run_id') || null,
    };
  } else if (params.get('run_id')) {
    focus = { kind: 'run', id: params.get('run_id') };
  } else if (params.get('conversation_id')) {
    focus = { kind: 'conversation', id: params.get('conversation_id') };
  } else if (page === 'evals' && params.get('eval')) {
    // An eval's page: "this eval" means this one. The eval tools take its id.
    focus = { kind: 'eval', id: params.get('eval') };
  } else if (page === 'dashboards') {
    // A dashboard's page: "this dashboard", "the second card" mean this one.
    // The dashboard tools take its id; the tab says which cards are in view.
    const m = pathname.match(/\/reports\/(\d+)/);
    if (m) focus = { kind: 'dashboard', id: m[1], ...(params.get('tab') ? { tab_id: params.get('tab') } : {}) };
  }

  const timeStart = params.get('time_start');
  const timeEnd = params.get('time_end');
  const preset = params.get('preset') || (page === 'dashboards' ? params.get('t') : null);

  const filters = [
    ...(decodeJsonParam(params.get('globalFilters')) || []),
    ...(decodeJsonParam(params.get('agentFilters')) || []),
    ...(decodeJsonParam(params.get('traceFilters')) || []),
  ];

  const agents = (params.get('agents') || '').split(',').filter(Boolean);
  const traces = (params.get('traces') || '').split(',').filter(Boolean);

  return {
    page,
    page_label: pageLabel,
    path: pathname,
    time_range: (timeStart || timeEnd || preset)
      ? { from: timeStart || null, to: timeEnd || null, preset: preset || null }
      : null,
    filters: filters.length ? filters : null,
    agents: agents.length ? agents : null,
    traces: traces.length ? traces : null,
    search: params.get('q') || null,
    focus,
  };
}

/** True when there is anything worth attaching (a bare page name is not). */
export function hasUsefulContext(ctx) {
  if (!ctx) return false;
  return Boolean(ctx.focus || ctx.filters || ctx.agents || ctx.traces || ctx.search || ctx.time_range);
}
